<template>
  <div class="text-left mb-[20px]">
    <div
      v-if="payload.prompt"
      class="markdown-body inline-block max-w-[75%] text-[#2E2E2E] text-left mb-[10px]"
      v-html="renderMarkdown(payload.prompt)"
    ></div>

    <div class="interactive-card max-w-[75%] bg-[#F2F2F2] rounded-[15px] p-[16px]">
      <!-- question: selectable options (checkboxes when multi) -->
      <div v-if="payload.kind === 'question'">
        <label
          v-for="opt in payload.options"
          :key="opt.id"
          class="flex items-start mb-[8px]"
          :class="locked ? 'cursor-default opacity-70' : 'cursor-pointer'"
        >
          <input
            :type="payload.multi ? 'checkbox' : 'radio'"
            :name="payload.interactionId"
            :checked="isSelected(opt.id)"
            :disabled="locked"
            class="mt-[3px] mr-[8px]"
            @change="toggle(opt.id)"
          />
          <span>
            <span class="text-[#2E2E2E]">{{ opt.label }}</span>
            <span v-if="opt.description" class="block text-xs text-[#6B6B6B]">{{ opt.description }}</span>
          </span>
        </label>
        <button
          type="button"
          :disabled="locked || selected.length === 0"
          class="mt-[8px] px-[16px] py-[8px] rounded-[15px] bg-[#2E2E2E] text-[#ffffff] border-transparent disabled:opacity-40"
          @click="submitQuestion"
        >
          {{ payload.confirmLabel || 'Enviar' }}
        </button>
      </div>

      <!-- confirm: preview table + confirm/reject -->
      <div v-else-if="payload.kind === 'confirm'">
        <div v-if="payload.preview" class="mb-[10px]">
          <div v-if="payload.preview.title" class="font-bold mb-[6px] text-[#2E2E2E]">
            {{ payload.preview.title }}
          </div>
          <div class="overflow-x-auto">
            <table class="border-collapse w-full text-sm">
              <thead v-if="headerRow.length">
                <tr>
                  <th
                    v-for="(cell, ci) in headerRow"
                    :key="ci"
                    class="border border-[#E5E5E5] bg-[#F7F7F7] px-[8px] py-[5px] text-left whitespace-nowrap"
                  >{{ cell }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in bodyRows" :key="ri">
                  <td
                    v-for="(cell, ci) in row"
                    :key="ci"
                    class="border border-[#E5E5E5] px-[8px] py-[5px] whitespace-nowrap"
                  >{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="flex gap-[8px]">
          <button
            type="button"
            :disabled="locked"
            class="px-[16px] py-[8px] rounded-[15px] bg-[#2E2E2E] text-[#ffffff] border-transparent disabled:opacity-40"
            @click="submitConfirm('confirm')"
          >
            {{ payload.confirmLabel || 'Confirmar' }}
          </button>
          <button
            type="button"
            :disabled="locked"
            class="px-[16px] py-[8px] rounded-[15px] bg-[#ffffff] text-[#2E2E2E] border border-[#E5E5E5] disabled:opacity-40"
            @click="submitConfirm('reject')"
          >
            {{ payload.rejectLabel || 'Rechazar' }}
          </button>
        </div>
      </div>

      <!-- form: editable fields, grouped by section, submitted with one button -->
      <div v-else-if="payload.kind === 'form'">
        <div v-for="(group, gi) in sections" :key="gi" class="mb-[6px]">
          <div v-if="group.name" class="font-bold text-sm text-[#2E2E2E] mt-[6px] mb-[8px]">
            {{ group.name }}
          </div>

          <div v-for="f in group.fields" :key="f.key" class="mb-[10px]">
            <!-- boolean renders its label beside the checkbox -->
            <label v-if="f.type === 'boolean'" class="flex items-start" :class="locked ? 'cursor-default' : 'cursor-pointer'">
              <input
                type="checkbox"
                :checked="!!form[f.key]"
                :disabled="locked"
                class="mt-[3px] mr-[8px]"
                @change="form[f.key] = $event.target.checked"
              />
              <span class="text-[#2E2E2E]">
                {{ f.label }}<span v-if="f.required" class="text-[#F61212]"> *</span>
              </span>
            </label>

            <template v-else>
              <label class="block text-xs text-[#6B6B6B] mb-[4px]">
                {{ f.label }}<span v-if="f.required" class="text-[#F61212]"> *</span>
              </label>

              <!-- multiselect: checkbox list, submits string[] -->
              <div v-if="f.type === 'multiselect'">
                <label
                  v-for="(o, oi) in (f.options || [])"
                  :key="oi"
                  class="flex items-start mb-[6px]"
                  :class="locked ? 'cursor-default' : 'cursor-pointer'"
                >
                  <input
                    type="checkbox"
                    :checked="(form[f.key] || []).includes(optionValue(o))"
                    :disabled="locked"
                    class="mt-[3px] mr-[8px]"
                    @change="toggleMulti(f.key, optionValue(o))"
                  />
                  <span class="text-[#2E2E2E]">{{ optionLabel(o) }}</span>
                </label>
              </div>

              <select
                v-else-if="f.type === 'select'"
                :value="form[f.key]"
                :disabled="locked"
                class="w-full px-[10px] py-[8px] rounded-[10px] border border-[#E5E5E5] bg-[#ffffff] text-[#2E2E2E]"
                @change="form[f.key] = $event.target.value"
              >
                <option v-for="(o, oi) in (f.options || [])" :key="oi" :value="optionValue(o)">
                  {{ optionLabel(o) }}
                </option>
              </select>

              <textarea
                v-else-if="f.type === 'textarea'"
                :value="form[f.key]"
                :disabled="locked"
                :placeholder="f.placeholder"
                rows="3"
                class="w-full px-[10px] py-[8px] rounded-[10px] border border-[#E5E5E5] bg-[#ffffff] text-[#2E2E2E]"
                @input="form[f.key] = $event.target.value"
              ></textarea>

              <input
                v-else
                :type="f.type === 'number' ? 'number' : 'text'"
                :value="form[f.key]"
                :disabled="locked"
                :placeholder="f.placeholder"
                class="w-full px-[10px] py-[8px] rounded-[10px] border border-[#E5E5E5] bg-[#ffffff] text-[#2E2E2E]"
                @input="form[f.key] = $event.target.value"
              />
            </template>

            <div v-if="f.help" class="text-xs text-[#9A9A9A] mt-[4px]">{{ f.help }}</div>
          </div>
        </div>

        <div class="flex gap-[8px]">
          <button
            type="button"
            :disabled="locked || !formValid"
            class="px-[16px] py-[8px] rounded-[15px] bg-[#2E2E2E] text-[#ffffff] border-transparent disabled:opacity-40"
            @click="submitForm('confirm')"
          >
            {{ payload.confirmLabel || 'Enviar' }}
          </button>
          <button
            type="button"
            :disabled="locked"
            class="px-[16px] py-[8px] rounded-[15px] bg-[#ffffff] text-[#2E2E2E] border border-[#E5E5E5] disabled:opacity-40"
            @click="submitForm('reject')"
          >
            {{ payload.rejectLabel || 'Cancelar' }}
          </button>
        </div>
      </div>

      <!-- prompt: free text -->
      <div v-else-if="payload.kind === 'prompt'">
        <textarea
          v-if="payload.multiline"
          v-model="text"
          :placeholder="payload.placeholder"
          :disabled="locked"
          rows="3"
          class="w-full px-[10px] py-[8px] rounded-[10px] border border-[#E5E5E5] bg-[#ffffff] text-[#2E2E2E]"
        ></textarea>
        <input
          v-else
          v-model="text"
          :placeholder="payload.placeholder"
          :disabled="locked"
          class="w-full px-[10px] py-[8px] rounded-[10px] border border-[#E5E5E5] bg-[#ffffff] text-[#2E2E2E]"
        />
        <button
          type="button"
          :disabled="locked || !text.trim()"
          class="mt-[8px] px-[16px] py-[8px] rounded-[15px] bg-[#2E2E2E] text-[#ffffff] border-transparent disabled:opacity-40"
          @click="submitPrompt"
        >
          {{ payload.confirmLabel || 'Enviar' }}
        </button>
      </div>

      <!-- what was chosen, once answered (locally or from persisted state) -->
      <div v-if="resultSummary" class="mt-[10px] text-sm text-[#6B6B6B]">
        {{ resultSummary }}
      </div>
      <div v-else-if="locked" class="mt-[10px] text-xs text-[#9A9A9A]">
        Esta pregunta ya no está disponible.
      </div>
    </div>
  </div>
</template>

<script>
import { renderMarkdown } from '../utils/markdown';
import { isInteractionOpen, summarizeAnswer } from '../utils/interactions';

export default {
  name: 'ChatInteractive',
  props: {
    msg: {
      type: Object,
      required: true
    }
  },
  emits: ['answer'],
  data() {
    return {
      selected: [],
      form: this.initialForm(),
      text: ''
    }
  },
  computed: {
    payload() {
      return this.msg.payload || {}
    },
    // Controls are read-only unless the interaction is still open.
    locked() {
      return !isInteractionOpen(this.msg)
    },
    headerRow() {
      const rows = this.payload.preview?.rows || []
      return rows.length ? rows[0] : []
    },
    bodyRows() {
      const rows = this.payload.preview?.rows || []
      return rows.slice(1)
    },
    // Fields grouped by `section` (ungrouped fields keep their original order first).
    sections() {
      const groups = []
      for (const f of (this.payload.fields || [])) {
        const name = f.section || ''
        let group = groups.find(g => g.name === name)
        if (!group) {
          group = { name, fields: [] }
          groups.push(group)
        }
        group.fields.push(f)
      }
      return groups
    },
    formValid() {
      return (this.payload.fields || []).every(f => {
        if (!f.required) return true
        const v = this.form[f.key]
        if (f.type === 'multiselect') return Array.isArray(v) && v.length > 0
        if (f.type === 'boolean') return true
        return String(v ?? '').trim() !== ''
      })
    },
    // Locally-submitted summary, or the persisted answer from history.
    resultSummary() {
      if (this.msg.answerSummary) return this.msg.answerSummary
      const stored = this.msg.interaction?.answer
      return stored ? summarizeAnswer(this.payload, stored) : ''
    }
  },
  methods: {
    renderMarkdown,
    initialForm() {
      const form = {}
      for (const f of (this.msg.payload?.fields || [])) {
        if (f.type === 'multiselect') {
          form[f.key] = Array.isArray(f.value) ? [...f.value] : []
        } else if (f.type === 'boolean') {
          form[f.key] = !!f.value
        } else if (f.type === 'select') {
          // Default to the first option so the submitted value matches the shown one.
          const first = (f.options || [])[0]
          form[f.key] = f.value ?? (first !== undefined ? this.optionValue(first) : '')
        } else {
          form[f.key] = f.value ?? ''
        }
      }
      return form
    },
    optionValue(o) {
      return typeof o === 'object' ? (o.id ?? o.value ?? o.label) : o
    },
    optionLabel(o) {
      return typeof o === 'object' ? (o.label ?? o.id ?? o.value) : o
    },
    isSelected(id) {
      return this.selected.includes(id)
    },
    toggle(id) {
      if (this.locked) return
      if (this.payload.multi) {
        const i = this.selected.indexOf(id)
        if (i >= 0) this.selected.splice(i, 1)
        else this.selected.push(id)
      } else {
        this.selected = [id]
      }
    },
    toggleMulti(key, value) {
      if (this.locked) return
      const current = Array.isArray(this.form[key]) ? this.form[key] : []
      const i = current.indexOf(value)
      if (i >= 0) current.splice(i, 1)
      else current.push(value)
      this.form[key] = current
    },
    emitAnswer(answer) {
      this.$emit('answer', {
        msg: this.msg,
        answer,
        summary: summarizeAnswer(this.payload, answer)
      })
    },
    submitQuestion() {
      if (this.locked || this.selected.length === 0) return
      this.emitAnswer({ optionIds: [...this.selected] })
    },
    submitConfirm(decision) {
      if (this.locked) return
      this.emitAnswer({ decision })
    },
    submitForm(decision) {
      if (this.locked) return
      if (decision === 'reject') {
        this.emitAnswer({ decision: 'reject' })
        return
      }
      if (!this.formValid) return

      // Coerce number fields back to numbers; everything else goes as-is.
      const fields = {}
      for (const f of (this.payload.fields || [])) {
        const v = this.form[f.key]
        fields[f.key] = f.type === 'number' && v !== '' && v !== null ? Number(v) : v
      }
      this.emitAnswer({ decision: 'confirm', fields })
    },
    submitPrompt() {
      const value = this.text.trim()
      if (this.locked || !value) return
      this.emitAnswer({ text: value })
    }
  }
}
</script>

<style scoped>
/* Inputs are content-box by default here (no Tailwind preflight), so w-full + padding would
   overflow the card's right edge. Force border-box so fields sit inside the padding. */
.interactive-card input,
.interactive-card select,
.interactive-card textarea {
  box-sizing: border-box;
  max-width: 100%;
}

.markdown-body :deep(p) { margin: 0 0 8px; }
.markdown-body :deep(p:last-child) { margin-bottom: 0; }
.markdown-body :deep(ul),
.markdown-body :deep(ol) { margin: 0 0 8px; padding-left: 20px; }
.markdown-body :deep(ul) { list-style: disc; }
.markdown-body :deep(ol) { list-style: decimal; }
.markdown-body :deep(strong) { font-weight: 700; }
.markdown-body :deep(em) { font-style: italic; }
.markdown-body :deep(a) { color: #1d4ed8; text-decoration: underline; word-break: break-word; }
.markdown-body :deep(code) {
  background: #F2F2F2; border-radius: 4px; padding: 1px 5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.9em;
}
</style>
